import type { PiniaPluginContext } from 'pinia'

export default defineNuxtPlugin(() => {
  const pinia = usePinia()

  pinia.use(({ store }: PiniaPluginContext) => {
    const loadedKeys = new Set<string>()
    const pendingPromises = new Map<string, Promise<void>>()

    store.ensureOnce = (key, loader, strategy = 'cache-first') => {
      if (loadedKeys.has(key) && strategy !== 'network-only') return Promise.resolve()

      const existingPromise = pendingPromises.get(key)
      if (existingPromise) return existingPromise

      const loadData = async () => {
        try {
          await loader()
          // Only mark loaded on success, so a failed request is retried next time
          loadedKeys.add(key)
        } finally {
          pendingPromises.delete(key)
        }
      }

      const pendingPromise = loadData()
      pendingPromises.set(key, pendingPromise)
      return pendingPromise
    }

    store.$clearEnsured = () => {
      loadedKeys.clear()
      pendingPromises.clear()
    }
  })
})

declare global {
  type EnsureStrategy = 'cache-first' | 'network-only'
}

declare module 'pinia' {
  interface PiniaCustomProperties {
    /**
     * Run `loader` once per session for `key` (`cache-first`), or always (`network-only`),
     * deduplicating concurrent calls. Only successful loads are cached; the plugin tracks
     * load state internally and invalidation happens via `$clearEnsured()`.
     */
    ensureOnce(key: string, loader: () => Promise<void>, strategy?: EnsureStrategy): Promise<void>
    /** Drop all `ensureOnce` cached and pending entries for this store. */
    $clearEnsured(): void
  }
}
